import { useState, useRef, useCallback, useEffect } from 'react'
import { Send, Paperclip, Mic, MicOff, SmilePlus } from 'lucide-react'
import { supabase } from '../../services/supabase'

const MAX_FILE_BYTES = 20 * 1024 * 1024  // 20MB
const BUCKET = 'task_attachments'         // reuse existing bucket

const EMOJI_LIST = ['😀','😂','😍','🤔','😢','😮','👍','👎','❤️','🔥','🎉','🙏','👀','💪','🤝']

function detectType(mime) {
  if (!mime) return 'file'
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('audio/')) return 'voice'
  return 'file'
}

export function DMMessageInput({ conversationId, replyToId, onSend, onTyping, isDarkMode }) {
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [recording, setRecording] = useState(false)
  const [showEmoji, setShowEmoji] = useState(false)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const fileInputRef = useRef(null)

  // Stop mic stream on unmount to prevent resource leak
  useEffect(() => () => { recorderRef.current?.stop() }, [])

  const uploadFile = async (file) => {
    const ext = file.name.split('.').pop()
    const path = `dm/${conversationId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type })
    if (error) throw error
    const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path)
    return publicUrl
  }

  const handleSend = useCallback(async (overridePayload) => {
    const payload = overridePayload ?? {}
    const body = payload.body ?? text.trim()
    const messageType = payload.messageType ?? 'text'
    if (!body && !payload.mediaUrl) return

    setSending(true)
    try {
      await onSend({
        body: messageType === 'text' ? body : null,
        messageType,
        mediaUrl: payload.mediaUrl ?? null,
        mediaName: payload.mediaName ?? null,
        mediaSize: payload.mediaSize ?? null,
        mediaMime: payload.mediaMime ?? null,
        replyToId,
      })
      setText('')
    } catch {
      // error stays in input; caller handles retry
    } finally {
      setSending(false)
    }
  }, [text, replyToId, onSend])

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_FILE_BYTES) { alert('File exceeds 20MB limit'); return }
    setSending(true)
    try {
      const mediaUrl = await uploadFile(file)
      await handleSend({
        messageType: detectType(file.type),
        mediaUrl,
        mediaName: file.name,
        mediaSize: file.size,
        mediaMime: file.type,
      })
    } catch {
      alert('File upload failed')
    } finally {
      setSending(false)
    }
  }

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = e => chunksRef.current.push(e.data)
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' })
        setSending(true)
        try {
          const mediaUrl = await uploadFile(file)
          await handleSend({ messageType: 'voice', mediaUrl, mediaName: file.name, mediaSize: file.size, mediaMime: file.type })
        } catch { alert('Voice upload failed') } finally { setSending(false) }
      }
      recorder.start()
      recorderRef.current = recorder
      setRecording(true)
    } catch {
      // microphone permission denied — button is hidden for unsupported browsers
    }
  }

  const stopRecording = () => {
    recorderRef.current?.stop()
    setRecording(false)
  }

  const hasMic = !!navigator.mediaDevices?.getUserMedia

  return (
    <div className={`flex-shrink-0 border-t px-3 py-2 ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-white'}`}>
      {/* Emoji picker */}
      {showEmoji && (
        <div className={`flex flex-wrap gap-1 p-2 mb-2 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
          {EMOJI_LIST.map(e => (
            <button key={e} onClick={() => { setText(t => t + e); setShowEmoji(false) }} className="text-lg hover:scale-125 transition-transform">
              {e}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <button
          onClick={() => setShowEmoji(v => !v)}
          className={`p-2 rounded-lg flex-shrink-0 transition ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100'}`}
        >
          <SmilePlus size={18} />
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          className={`p-2 rounded-lg flex-shrink-0 transition ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100'}`}
        >
          <Paperclip size={18} />
        </button>
        <input ref={fileInputRef} type="file" accept="*/*" className="hidden" onChange={handleFileChange} />

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); onTyping?.() }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
          }}
          placeholder="Type a message…"
          rows={1}
          className={`flex-1 resize-none rounded-xl px-3 py-2 text-sm outline-none border transition ${
            isDarkMode
              ? 'bg-slate-800 border-slate-700 text-slate-100 placeholder-slate-500'
              : 'bg-gray-50 border-gray-200 text-sfp-darkgray placeholder-gray-400'
          }`}
          style={{ maxHeight: 120, overflowY: 'auto' }}
        />

        {hasMic && (
          <button
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            className={`p-2 rounded-lg flex-shrink-0 transition ${
              recording ? 'bg-red-500 text-white' : isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100'
            }`}
            title="Hold to record voice"
          >
            {recording ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        )}

        <button
          onClick={() => handleSend()}
          disabled={(!text.trim()) || sending}
          className="p-2 rounded-xl bg-sfp-orange text-white disabled:opacity-40 hover:bg-orange-600 transition flex-shrink-0"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  )
}
